/**
 * QuoteEditorView — 견적 편집 지면 페이지 (P1: 지면 + 편집 레이어).
 *   /deal/:dealId/quote/:quoteId  → 편집 데이터 1 fetch → 어댑터(api → QuoteItem)
 *   → fillPartPaper 로 채워진 지면 + 지면 밖 줄들 → paper-3-html(지면) + render-our-view-out-paper(여백·요약 카드·하단바 — 우리 뷰 지면 밖 세 겹).
 *   [고객 뷰] = 루트 클래스 하나로 편집 세 겹을 숨긴다(설계 §3.3). 행의 세계는 지면 안(우리 줄·소스 팝오버·매입 후보 패널)에 있다.
 *   (설계: docs/QUOTE_ONEPAGE_DESIGN.md §3 · ServerConfigView 별탭 관례의 두 번째 적용)
 *
 * 서버 견적 지면도 같은 채움 파이프라인을 탄다(fillServerPaper).
 *
 * 파일 배치는 하향식: ①명사(화면 상태) → ②국경(api → 화면 명사) → ③View
 * (생명주기 → 데이터 반영 → 그리기 → 이벤트) → ④편집 재료 → ⑤지면 비교 → ⑥CSS.
 */
import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import { assert } from '../../lib/assert';
import * as api from '../../api/quote-api';
import { renderPaper, renderPaperRows, PAPER_CSS } from './paper-3-html';
import { toCustomerPaper } from './paper-3-xlsx';
import type { PaperTemplate, InPaper } from './paper-0-cells';
import { fillPartPaper, fillServerPaper, fillPartRentalPaper, fillServerRentalPaper, toServerConfigs, toRentalServerConfigs, extraPartsOf, PART_MAP, SERVER_MAP, PART_RENTAL_MAP, SERVER_RENTAL_MAP, type FilledPaper } from './paper-2-fill';
import { type QuoteItem, type QuoteCtx, type QuoteTitle, type SourceType, type CostCandidate, NO_COST_CANDIDATES, sellUnitKrw, costCandidatesOf, costUnitKrw } from './paper-1-specs';
import { renderGutter, renderSummaryCard, renderBottomBar, OUR_VIEW_OUT_PAPER_CSS, type OurViewState } from './render-our-view-out-paper';
import { renderSourcePicker, SOURCE_CSS, type SourcePickerState } from './render-source';
import { renderAddItemsModal, ADD_ITEMS_CSS, type AddItemsState, type NewItem, type ServerChip } from './render-add-items';
import { renderCostCandidatesPanel, COST_CANDIDATES_CSS, type CostHintState } from './render-cost-candidates';
import { searchAll } from '../../api/catalog-api';
import { toSpecCategory } from '@hcltech/shared/server-config/spec-category';
import type { DealId, QuoteId } from '../../lib/brand';
import { fetchSourceCandidates, searchSourceCandidates, type SourcesResponse } from '../../api/server-config-api';
import PARTS_TEMPLATE from './templates/quote.parts.template.json';
import SERVER_TEMPLATE from './templates/quote.server.template.json';
import PARTS_RENTAL_TEMPLATE from './templates/quote.parts-rental.template.json';
import SERVER_RENTAL_TEMPLATE from './templates/quote.server-rental.template.json';

/* ═══════════════ ① 명사 — 화면 상태 (살림은 this.data 한 곳뿐) ═══════════════ */

interface QuoteEditorViewData {
  quoteId: QuoteId;
  dealId: DealId;             // 소스 후보 조회(/sources?deal_id=) 용. branded ID 시범(컨벤션 1.6) — 경계에서 도장, 맞바꿈은 컴파일 에러
  isLoaded: boolean;
  docType: 'part' | 'server' | null;
  isRental: boolean;           // 임대 견적(quote_type=RENTAL) — 파트는 임대 지면, 서버는 후속(현재 판매 서식 + 안내)
  header: api.QuoteEditorApiHeader | null;
  allItems: QuoteItem[];       // 서버행 포함 전체(재계산 원본)
  items: QuoteItem[];          // 편집 레이어가 정렬하는 flat 부품 (data-idx 의 순서 계약)
  filled: FilledPaper | null;
  ctx: QuoteCtx;
  // 편집 상태
  selectedIdx: number | null;
  seen: number[];             // 사람이 여백 카드를 클릭해 검토한 행(데모, 영속은 307 후속)
  isCustomerView: boolean;
  isBrief: boolean;   // 약식(서버 요약) on/off
  sourcePicker: SourcePickerState | null;             // 소스 선택 팝오버 살림 — null=닫힘
  addItems: AddItemsState | null;                     // 품목 추가 모달 살림 — null=닫힘
  costHint: CostHintState | null;                     // 매입 후보 패널 살림 — null=닫힘
  isCostCandidatesLoaded: boolean;                    // 파트별 매입 후보 백그라운드 로드 끝(실패 포함)
}

/* ═══════════════ ② 국경 — api 어휘(snake) → 화면 명사 (도장은 여기 한 곳) ═══════════════ */

/** 편집 데이터 행(api·snake) → 지면 행 모델(QuoteItem). 스페어/어셈블리는 카탈로그 조인 후속. */
function toQuoteItem(apiRow: api.QuoteEditorApiRow): QuoteItem {
  return {
    id: apiRow.id ? Number(apiRow.id) : null,
    category: apiRow.category,
    optionPart: apiRow.option_part,
    requestedPart: apiRow.requested_part,
    condition: apiRow.requested_condition,
    sparePart: null,
    assemblyPart: null,
    quantity: apiRow.quantity,
    unitPriceUsd: apiRow.unit_price_usd != null ? Number(apiRow.unit_price_usd) : null,
    unitPriceKrw: apiRow.unit_price_krw != null ? Number(apiRow.unit_price_krw) : null,
    isBasePart: apiRow.is_base_part,
    isUnregistered: apiRow.is_unregistered,
    parentId: apiRow.parent_quote_item_id ? Number(apiRow.parent_quote_item_id) : null,
    dealItemId: apiRow.deal_item_id ? Number(apiRow.deal_item_id) : null,
    parentDealItemId: apiRow.parent_deal_item_id ? Number(apiRow.parent_deal_item_id) : null,
    lineType: apiRow.line_type === 'server' ? 'server' : null,   // 경계 검증 — 그 외 값은 부품 행
    costUsd: apiRow.cost_basis_currency === 'USD' && apiRow.cost_basis_amount != null ? Number(apiRow.cost_basis_amount)
           : (apiRow.cost_basis_usd != null ? Number(apiRow.cost_basis_usd) : null),
    costKrw: apiRow.cost_basis_currency === 'KRW' && apiRow.cost_basis_amount != null ? Number(apiRow.cost_basis_amount) : null,
    serverQuantity: apiRow.server_quantity,
    unitPriceOverride: apiRow.unit_price_override != null ? Number(apiRow.unit_price_override) : null,
    description: apiRow.description,
    sourceType: apiRow.source_type as SourceType | null,   // 경계 도장 — DB CHECK 가 정본
    sourceRefId: apiRow.source_ref_id,
    sourceName: apiRow.source_name ?? null,               // 경계 검증 — 옛 백엔드는 이 열이 없다
    sourceCondition: apiRow.source_condition ?? null,
    rentalTermMonths: apiRow.rental_term_months ?? null,  // 임대 견적만 채워진다(판매는 null)
    rentalContractCostKrw: apiRow.rental_contract_cost_krw != null ? Number(apiRow.rental_contract_cost_krw) : null,
  };
}

/** 매입가 원 환산 — USD 는 환율로, KRW 는 그대로, 없으면 null. */
function toCostKrw(amount: number | null, currency: string | null, krwPerUsd: number): number | null {
  if (amount == null) return null;
  return currency === 'USD' ? Math.round(amount * krwPerUsd) : amount;
}

/** 재고 한 건 → 매입 후보. 라벨 '컨디션 · N개'. */
function toStockCandidate(stock: SourcesResponse['stock'][number], krwPerUsd: number): CostCandidate {
  return {
    sourceType: 'stock', sourceRefId: stock.stock_id, label: `${stock.condition} · ${stock.qty_available}개`,
    costAmount: stock.cost_amount, costCurrency: stock.cost_currency, costUnitKrw: toCostKrw(stock.cost_amount, stock.cost_currency, krwPerUsd),
  };
}

/** RFQ 한 건 → 매입 후보. 라벨 'MM/DD · 공급사'. */
function toRfqCandidate(rfq: SourcesResponse['rfq'][number], krwPerUsd: number): CostCandidate {
  const monthDay = (rfq.rfq_date ?? '').slice(5, 10).replace('-', '/');
  return {
    sourceType: rfq.source_type, sourceRefId: rfq.rfq_id, label: `${monthDay ? `${monthDay} · ` : ''}${rfq.supplier_name}`,
    costAmount: rfq.price_amount, costCurrency: rfq.price_currency, costUnitKrw: toCostKrw(rfq.price_amount, rfq.price_currency, krwPerUsd),
  };
}

/**
 * 소스 후보 응답(api) → 파트넘버별 매입 후보들.
 * 재고는 파트넘버가 필수(스키마)라 전부 후보. RFQ 는 파트넘버가 없을 수 있고, 그런 RFQ 는 어느 품목에도 못 붙으니 후보가 아니다.
 */
function toCostCandidates(src: SourcesResponse, krwPerUsd: number): Map<string, CostCandidate[]> {
  const stockCandidates = src.stock.map((stock) => [stock.option_part, toStockCandidate(stock, krwPerUsd)] as const);
  const rfqWithPart = src.rfq.filter((rfq): rfq is typeof rfq & { option_part: string } => rfq.option_part != null);
  const rfqCandidates = rfqWithPart.map((rfq) => [rfq.option_part, toRfqCandidate(rfq, krwPerUsd)] as const);

  const costCandidates = new Map<string, CostCandidate[]>();   // 파트넘버 → 그 파트의 후보들
  for (const [part, candidate] of [...stockCandidates, ...rfqCandidates]) {
    costCandidates.set(part, [...(costCandidates.get(part) ?? []), candidate]);
  }
  return costCandidates;
}

/** 편집 데이터 헤더(api·snake) → 표제(QuoteTitle). null 헤더 = 표제 없음(헤더 칸은 틀 문구 유지). */
function toQuoteTitle(header: api.QuoteEditorApiHeader | null): QuoteTitle | undefined {
  if (!header) return undefined;
  return {
    dealName: header.deal_name, quoteNo: header.quote_no, customerName: header.customer_name,
    issuedAt: header.issued_at, validUntil: header.valid_until,
    contactName: header.contact_name, contactPhone: header.contact_phone, contactEmail: header.contact_email,
    assigneeName: header.assignee_name, assigneeEmail: header.assignee_email,
  };
}

/* ═══════════════ ③ View — 생명주기 → 데이터 반영 → 그리기 → 이벤트 ═══════════════ */

export class QuoteEditorView extends SolaView<QuoteEditorViewData> {
  override events = {
    click: {
      '@qe-edit': 'onEdit',
      '@qe-row':  'onRow',
      '@qe-view': 'onCustomerViewToggle',
      '@qe-brief': 'onBriefToggle',
      '@qe-save': 'onSave',
      '@qe-export-xlsx': 'onExportXlsx',
      '@qe-send': 'onSend',
      '@qe-source':    'onSource',
      '@qe-source-tab':    'onSourceTab',
      '@qe-source-search': 'onSourceSearch',
      '@qe-source-pick':  'onSourcePick',
      '@qe-source-close': 'onSourceClose',
      '@qe-source-rfq':   'onSourceRfq',
      '@qe-add-open':               'onAddOpen',
      '@qe-add-target':             'onAddTarget',
      '@qe-add-to-basket':          'onAddToBasket',
      '@qe-add-remove-from-basket': 'onAddRemoveFromBasket',
      '@qe-add-quantity':           'onAddQuantity',
      '@qe-add-confirm':            'onAddConfirm',
      '@qe-add-close':              'onAddClose',
      '@qe-cost-hint':        'onCostHint',
      '@qe-cost-hint-fill':   'onCostHintFill',
      '@qe-cost-hint-pick':   'onCostHintPick',
      '@qe-cost-hint-close':  'onCostHintClose',
    },
    input: {
      '@qe-add-query': 'onAddQueryInput',       // 타이핑 즉시 카탈로그 검색
    },
    mousedown: {
      '.qc-panel': 'onCostHintKeepFocus',        // 레퍼런스 패널 클릭이 판매단가 입력창 포커스를 뺏지 않게 (blur→닫힘 방지)
    },
    keydown: {
      '@qe-source-query': 'onSourceQueryKey',   // 검색 입력줄 Enter
      '@qe-add-query':    'onAddQueryKey',      // Esc 닫기
    },
  };

  private _shortcutKeyListener?: (e: KeyboardEvent) => void;
  private _addSearchTimer: number | undefined;   // 품목 검색 타이핑 지연(250ms)

  constructor(opts: { quoteId: string }) {
    super();
    this.data = {
      quoteId: opts.quoteId as QuoteId,   // 라우트 파라미터 경계 — 도장 1회
      dealId: '' as DealId,
      isLoaded: false,
      docType: null,
      isRental: false,
      header: null,
      allItems: [],
      items: [],
      filled: null,
      ctx: { krwPerUsd: 0, costCandidates: NO_COST_CANDIDATES },
      selectedIdx: null,
      seen: [],
      isCustomerView: false,
      isBrief: false,
      sourcePicker: null,
      addItems: null,
      costHint: null,
      isCostCandidatesLoaded: false,
    };
  }

  render(): HTMLElement {
    return htmlSolaWithInstance(this)`
      <div class="quote-editor">
        <div class="qe-status">불러오는 중…</div>
        <div class="qe-shell">
          <div class="qe-gutter-slot qe-slot"></div>
          <div class="qe-paper-wrap"></div>
          <div class="qe-summary-slot qe-slot"></div>
          <div class="qe-cost-hint-slot"></div>
        </div>
        <div class="qe-bottom-slot"></div>
        <button class="qe-view-return" type="button" data-action="qe-view">✎ 우리 뷰로</button>
        <div class="qe-source-slot"></div>
        <div class="qe-add-items-slot"></div>
      </div>`;
  }

  override onMount(): void {
    this.injectStyles();
    this.connectShortcutKeys();
    void this.load();
  }

  /** 지면·편집 CSS 4벌을 <style> 로 주입 — 템플릿 리터럴의 ${}·백틱 이스케이프 회피. */
  private injectStyles(): void {
    const styleTag = document.createElement('style');
    styleTag.textContent = BASE_CSS + PAPER_CSS + OUR_VIEW_OUT_PAPER_CSS + SOURCE_CSS + ADD_ITEMS_CSS + COST_CANDIDATES_CSS;
    this._rootElement?.appendChild(styleTag);
  }

  /** 단축키 연결 — Ctrl/Cmd+S 저장 · Ctrl/Cmd+K 품목 추가. 해제는 onUnmount 가 _shortcutKeyListener 로. */
  private connectShortcutKeys(): void {
    this._shortcutKeyListener = (keyEvent: KeyboardEvent) => {
      if (!(keyEvent.ctrlKey || keyEvent.metaKey)) return;
      const key = keyEvent.key.toLowerCase();
      if (key === 's') { keyEvent.preventDefault(); this.onSave(); }
      if (key === 'k') { keyEvent.preventDefault(); this.openAddItems(null); }
    };
    window.addEventListener('keydown', this._shortcutKeyListener);
  }

  override onUnmount(): void {
    if (this._shortcutKeyListener) window.removeEventListener('keydown', this._shortcutKeyListener);
  }

  /* ── 데이터 → 지면 (재계산) ── */

  private async load(): Promise<void> {
    try {
      const editorData = await api.fetchQuoteEditorData(this.data.quoteId);
      this.data.ctx = { krwPerUsd: editorData.fx, costCandidates: NO_COST_CANDIDATES };   // API 필드명은 fx 유지 — 경계 번역. 후보는 로드 뒤
      this.data.dealId = editorData.deal_id as DealId;   // API 경계 — 도장 1회
      this.data.isLoaded = true;
      this.applyEditorData(editorData);
      void this.loadCostCandidates();   // 매입 후보(재고·RFQ) — 백그라운드 1회, 오면 우리 줄 꼬리만 갈아 끼움
      // 파트·서버 임대 모두 임대 지면으로 그린다(P1 렌더). 임대 입력 편집(기간·계산식·수동단가)은 후속(P2).
      this.status('');
    } catch (error) {
      this.status(error instanceof Error ? error.message : '견적을 불러오지 못했습니다.');
    }
  }

  /** 편집 데이터(fetch/PATCH 응답) → allItems·헤더·유형 반영 후 재계산·재렌더. */
  private applyEditorData(editorData: api.QuoteEditorApiData): void {
    this.data.docType = editorData.doc_type;
    this.data.isRental = editorData.is_rental;
    this.data.header = editorData.header;
    this.data.allItems = editorData.rows.map(toQuoteItem);
    this.refillAndRepaint();
  }

  /** 재계산 → 다시 그림 — 편집 후의 정석 경로. 이전 지면을 잡아둬 지면은 달라진 행만 갈아 끼운다. */
  private refillAndRepaint(): void {
    const prevInPaper = this.data.filled?.inPaper ?? null;
    this.refill();
    this.repaint(prevInPaper);
  }

  /** allItems → filled. 현재 뷰로 채워 상태에 앉힌다 — 편집 후 재계산에 재사용. */
  private refill(): void {
    const { items, filled } = this.fillPaper(this.data.isCustomerView);
    this.data.items = items;
    this.data.filled = filled;
  }

  /**
   * 지면 채움 — this.data 를 읽기만 하는 순수 계산(part/server 분기). 상태는 안 건드린다.
   * items 는 지면에 실린 품목 순서(편집 data-idx 계약 — 서버는 구성 순으로 납작하게).
   * 화면(refill — 현재 뷰)과 xlsx(onExportXlsx — 언제나 고객 뷰)가 같은 손을 쓴다.
   */
  private fillPaper(isCustomerView: boolean): { items: QuoteItem[]; filled: FilledPaper } {
    const ctx = this.data.ctx;
    const quoteTitle = toQuoteTitle(this.data.header);
    if (this.data.docType === 'server') {
      const extraParts = extraPartsOf(this.data.allItems);         // 어느 서버에도 안 묶인 개별 파트
      if (this.data.isRental) {   // 서버 임대 지면 — 하이브리드(구성 밴드 + 파트식 SUMMARY). 언제나 약식.
        const configs = toRentalServerConfigs(this.data.allItems, ctx);
        const filled = fillServerRentalPaper(SERVER_RENTAL_TEMPLATE as unknown as PaperTemplate, SERVER_RENTAL_MAP, configs, extraParts, ctx, quoteTitle);
        return { items: [...configs.flatMap((config) => config.items), ...extraParts], filled };
      }
      const configs = toServerConfigs(this.data.allItems, ctx);   // 납작한 행들 → 서버 구성들
      const filled = fillServerPaper(SERVER_TEMPLATE as unknown as PaperTemplate, SERVER_MAP, configs, extraParts, ctx, isCustomerView, quoteTitle, this.data.isBrief);
      return { items: [...configs.flatMap((config) => config.items), ...extraParts], filled };
    }
    if (this.data.isRental) {   // 파트 임대 지면 — 기간·임대단가·월임대·금액합계
      const filled = fillPartRentalPaper(PARTS_RENTAL_TEMPLATE as unknown as PaperTemplate, PART_RENTAL_MAP, this.data.allItems, ctx, isCustomerView, quoteTitle);
      return { items: this.data.allItems, filled };
    }
    const filled = fillPartPaper(PARTS_TEMPLATE as unknown as PaperTemplate, PART_MAP, this.data.allItems, ctx, isCustomerView, quoteTitle);
    return { items: this.data.allItems, filled };
  }

  /* ── 그리기 (paint 가족) ── */

  /** 지면 + 편집 세 겹 다시 그림. prevInPaper 를 주면 지면은 달라진 행만. */
  private repaint(prevInPaper: InPaper | null = null): void {
    if (!this.data.filled) return;
    this.paintPaper(prevInPaper);
    this.paintOurViewOutPaper();
  }

  /**
   * 지면 그리기 — 계산은 통으로(refill), 교체는 달라진 행만 (실측: 행 비교 ~5ms · DOM 통재생성 회피).
   *   행 높이 나열이 같으면 → 행별 HTML 을 비교해 다른 행 포장(.paper-row)만 갈아 끼운다.
   *   다르면(첫 그림·품목 추가·줄 생김·뷰 전환) → 지면 통째로.
   *   편집 input 이 심긴 행은 모델이 같아도 교체 — 입력창을 걷어낸다.
   */
  private paintPaper(prevInPaper: InPaper | null): void {
    const inPaper = this.data.filled?.inPaper;
    if (!inPaper) return;
    const paperEl = this._rootElement?.querySelector<HTMLElement>('.qe-paper-wrap .paper');
    if (paperEl && prevInPaper && sameRowHeights(prevInPaper, inPaper)) this.paintChangedRows(paperEl, prevInPaper, inPaper);
    else this.paintWholePaper(inPaper);
  }

  /** 지면 통째 다시 그림 — 첫 그림, 또는 행 구성이 바뀐 경우(품목 추가·줄 생김·뷰 전환). */
  private paintWholePaper(inPaper: InPaper): void {
    this.refreshRegion('.qe-paper-wrap', () => renderPaper(inPaper));
  }

  /** 달라진 행만 갈아 끼움 — 행별 HTML 비교. 편집 input 이 심긴 행은 같아도 교체(입력창 걷어내기). */
  private paintChangedRows(paperEl: HTMLElement, prevInPaper: InPaper, inPaper: InPaper): void {
    const prevRows = renderPaperRows(prevInPaper);
    const nextRows = renderPaperRows(inPaper);
    for (let rowIndex = 0; rowIndex < nextRows.length; rowIndex++) {
      const rowEl = paperEl.children[rowIndex] as HTMLElement | undefined;
      if (!rowEl) continue;
      const hasEditorInput = rowEl.querySelector('input,select') != null;
      if (prevRows[rowIndex] !== nextRows[rowIndex] || hasEditorInput) this.refreshRegion(rowEl, nextRows[rowIndex]);
    }
  }

  /** 편집 세 겹(거터·요약 카드·하단바)만 다시 그린다 — 원래 작아서 통째 갱신. */
  private paintOurViewOutPaper(): void {
    const filled = this.data.filled;
    if (!filled) return;
    const ourViewState = this.ourViewState();
    this.refreshRegion('.qe-gutter-slot', () => renderGutter(filled.inPaper.rows, filled.outPaper.items, ourViewState));
    this.refreshRegion('.qe-summary-slot', () => renderSummaryCard(this.data.ctx, filled.outPaper.totals));
    this.refreshRegion('.qe-bottom-slot', () => renderBottomBar(this.data.items, filled.outPaper.totals, ourViewState));
  }

  private ourViewState(): OurViewState {
    return { selectedIdx: this.data.selectedIdx, seen: this.data.seen, isCustomerView: this.data.isCustomerView, isServer: this.data.docType === 'server', isBrief: this.data.isBrief };
  }

  private status(msg: string): void {
    const el = this._rootElement?.querySelector<HTMLElement>('.qe-status');
    if (el) { el.textContent = msg; el.style.display = msg ? '' : 'none'; }
  }

  /* ── 지면 칸 인라인 편집 — 클릭/Tab → 입력 → 커밋 → 재계산·재렌더 → 다음 칸 ── */

  onEdit(_e: Event, target: HTMLElement): void { this.openEditor(target); }

  /** 편집 칸에 입력기를 심는다 — 좌표 읽기 → 입력기 만들기 → 심기 → 커밋/취소 연결. */
  private openEditor(target: HTMLElement): void {
    if (target.querySelector('input,select')) return;        // 이미 편집 중
    const idx = Number(target.dataset.idx);
    const field = target.dataset.field as keyof QuoteItem;
    const kind = target.dataset.kind;
    const item = this.data.items[idx];
    if (!item || !field) return;
    const currentValue = item[field] as unknown as string | number | null;

    const input = editorInputFor(kind, field, currentValue);
    plantInput(target, input);
    if (field === 'unitPriceKrw') this.connectCostHintToEditor(idx, target, input as HTMLInputElement);   // 판매단가 — 매입 후보 패널이 따라 뜬다

    let isDone = false;                                      // Enter 뒤 blur 의 중복 커밋 방지
    connectEditorKeys(input,
      (dir) => {
        if (isDone) return; isDone = true;
        this.closeCostHint();
        this.applyEdit(item, idx, field, parseEditValue(kind, input.value, currentValue), dir);
      },
      () => { isDone = true; this.closeCostHint(); this.repaint(this.data.filled?.inPaper ?? null); });   // Escape — 입력창 심긴 행만 걷힘
  }

  /* ── 매입 후보 패널 (판매단가 편집 중 자동 · ⓘ 로 수동) ── */

  /** 판매단가 입력창에 패널을 붙인다 — 타이핑마다 입력가 기준 마진율을 다시 그린다. */
  private connectCostHintToEditor(itemIndex: number, cellEl: HTMLElement, input: HTMLInputElement): void {
    const item = this.data.items[itemIndex];
    this.openCostHint(itemIndex, sellUnitKrw(item, this.data.ctx), cellEl);
    input.addEventListener('input', () => {
      const hint = this.data.costHint;
      if (!hint) return;
      const typed = Number(input.value.replace(/[,\s]/g, ''));
      hint.sellUnitKrw = input.value.trim() === '' || Number.isNaN(typed) ? null : typed;
      this.paintCostHint();
    });
  }

  // ⓘ 클릭 — 편집 없이 지금 판매가 기준으로 패널.
  onCostHint(_e: Event, target: HTMLElement): void {
    const itemIndex = Number(target.dataset.idx);
    const item = this.data.items[itemIndex];
    if (!item) return;
    if (this.data.costHint?.itemIndex === itemIndex) { this.closeCostHint(); return; }   // 같은 ⓘ 다시 누르면 닫힘
    this.openCostHint(itemIndex, sellUnitKrw(item, this.data.ctx), target);
  }

  // 목표 마진 버튼 — 열린 입력창이 있으면 값만 채움(확정 아님), 없으면 그 칸 편집기를 열고 채움.
  onCostHintFill(_e: Event, target: HTMLElement): void {
    const hint = this.data.costHint;
    if (!hint) return;
    const value = target.dataset.value ?? '';
    const openInput = this._rootElement?.querySelector<HTMLInputElement>(`.pc-ed[data-idx="${hint.itemIndex}"][data-field="unitPriceKrw"] input`);
    if (openInput) { openInput.value = value; openInput.dispatchEvent(new Event('input')); openInput.focus(); return; }
    const cellEl = this._rootElement?.querySelector<HTMLElement>(`.pc-ed[data-idx="${hint.itemIndex}"][data-field="unitPriceKrw"]`);
    if (!cellEl) return;
    this.openEditor(cellEl);
    const input = cellEl.querySelector<HTMLInputElement>('input');
    if (input) { input.value = value; input.dispatchEvent(new Event('input')); }
  }

  onCostHintClose(): void { this.closeCostHint(); }

  // 레퍼런스 패널 안 클릭은 판매단가 입력창의 포커스를 뺏지 않는다 — blur 로 패널이 닫히거나(선택이 새면) 편집이 중간 확정되는 걸 막는다.
  onCostHintKeepFocus(event: Event): void { event.preventDefault(); }

  // 레퍼런스 [선택] — 소스 연결 + 편집 중이던 판매단가를 한 PATCH 로 함께 확정(두 동작이 아니다: 소스 줄·마진이 같이 갱신). 패널은 유지.
  async onCostHintPick(_e: Event, target: HTMLElement): Promise<void> {
    const hint = this.data.costHint;
    if (!hint) return;
    const item = this.data.items[hint.itemIndex];
    if (!item) return;
    if (item.id == null) { this.status('저장 안 된 새 품목입니다 — 소스 연결은 저장 후(후속).'); return; }
    const patch = sourcePickPatch(target.dataset, item);
    const openInput = this._rootElement?.querySelector<HTMLInputElement>(`.pc-ed[data-idx="${hint.itemIndex}"][data-field="unitPriceKrw"] input`);
    const typedPrice = openInput && openInput.value.trim() !== '' ? Number(openInput.value.replace(/[,\s]/g, '')) : null;
    if (typedPrice != null && Number.isFinite(typedPrice)) {   // 입력창에 판매단가가 있으면 KRW 네이티브로 같이 확정
      patch.unit_price_krw = typedPrice;
      patch.unit_price_usd = null;
    }
    await this.saveSourceLink(item, patch);
    this.paintCostHint();
  }

  /** 패널 열기 — 살림 채우고 기준 칸 바로 아래에 자리 잡는다(.qe-shell 기준 좌표). */
  private openCostHint(itemIndex: number, sellUnitKrwNow: number | null, anchorEl: HTMLElement): void {
    this.data.costHint = { itemIndex, sellUnitKrw: sellUnitKrwNow };
    this.paintCostHint();
    const shell = this._rootElement?.querySelector<HTMLElement>('.qe-shell');
    const slot = this._rootElement?.querySelector<HTMLElement>('.qe-cost-hint-slot');
    if (!shell || !slot) return;
    const shellRect = shell.getBoundingClientRect();
    const anchorRect = anchorEl.getBoundingClientRect();
    slot.style.left = `${anchorRect.left - shellRect.left}px`;
    slot.style.top = `${anchorRect.bottom - shellRect.top + 2}px`;
  }

  private closeCostHint(): void {
    if (!this.data.costHint) return;
    this.data.costHint = null;
    this.paintCostHint();
  }

  /** 패널을 살림대로 — 닫힘이면 빈 문자열. */
  private paintCostHint(): void {
    this.refreshRegion('.qe-cost-hint-slot', () => this.renderCostHintOverlay());
  }

  private renderCostHintOverlay(): string {
    const hint = this.data.costHint;
    if (!hint) return '';
    const item = this.data.items[hint.itemIndex];
    if (!item) return '';
    return renderCostCandidatesPanel(
      costCandidatesOf(item, this.data.ctx), hint.sellUnitKrw, item.sourceRefId, costUnitKrw(item, this.data.ctx), this.data.isCostCandidatesLoaded,
      item.optionPart ?? '',
    );
  }

  /** 파트별 매입 후보 — 모든 파트넘버를 한 요청으로. 오면 ctx 를 갈고 재채움(우리 줄 꼬리만 달라진다). 실패는 조용히. */
  private async loadCostCandidates(): Promise<void> {
    const parts = [...new Set(this.data.allItems.map((item) => item.optionPart).filter((part): part is string => part != null))];
    try {
      const src = await fetchSourceCandidates(parts, this.data.dealId);
      this.data.ctx = { ...this.data.ctx, costCandidates: toCostCandidates(src, this.data.ctx.krwPerUsd) };
    } catch {
      /* 후보 없이도 편집엔 지장 없다 */
    }
    this.data.isCostCandidatesLoaded = true;
    this.refillAndRepaint();
    if (this.data.costHint) this.paintCostHint();
  }

  /** 편집 반영 — 품목에 값 앉히고 → 재계산·재렌더 → 저장(PATCH) → Tab/Enter 면 다음 칸. */
  private applyEdit(item: QuoteItem, idx: number, field: keyof QuoteItem, value: string | number | null, dir: number): void {
    (item as unknown as Record<string, unknown>)[field] = value;
    if (field === 'unitPriceKrw') item.unitPriceUsd = null;   // 단가 편집 = KRW 네이티브
    if (!this.data.seen.includes(idx)) this.data.seen.push(idx);
    this.refillAndRepaint();
    void this.persist(item.id, this.patchOf(field, item));
    if (dir !== 0) this.focusRel(idx, field, dir);            // Tab/Enter → 다음 편집 칸
  }

  /** 현재 편집 칸 기준 dir(±1) 만큼 떨어진 편집 칸을 연다(재렌더 후 DOM 순서 기준). */
  private focusRel(idx: number, field: string, dir: number): void {
    const wrap = this._rootElement?.querySelector<HTMLElement>('.qe-paper-wrap');
    if (!wrap) return;
    const cells = Array.from(wrap.querySelectorAll<HTMLElement>('[data-action="qe-edit"]'));
    const pos = cells.findIndex((cellEl) => Number(cellEl.dataset.idx) === idx && cellEl.dataset.field === field);
    const next = pos >= 0 ? cells[pos + dir] : undefined;
    if (next) this.openEditor(next);
  }

  /** 편집 필드 → 서버 컬럼 PATCH 바디(필드 하나만). */
  private patchOf(field: keyof QuoteItem, item: QuoteItem): api.QuoteItemPatchBody {
    switch (field) {
      case 'quantity':     return { quantity: item.quantity };
      case 'unitPriceKrw': return { unit_price_krw: item.unitPriceKrw, unit_price_usd: null };
      case 'condition':    return { requested_condition: item.condition };
      case 'category':     return { category: item.category };
      case 'optionPart':   return { option_part: item.optionPart };
      case 'description':  return { description: item.description };
      default:             return {};
    }
  }

  /** 안전한 필드 단위 저장 — PATCH /quote/:id/item/:itemId. 새 행(id null)은 후속. */
  private async persist(itemId: number | null, patch: api.QuoteItemPatchBody): Promise<void> {
    if (itemId == null || Object.keys(patch).length === 0) return;
    try {
      await api.patchQuoteEditorItem(this.data.quoteId, itemId, patch);
    } catch (error) {
      this.status(error instanceof Error ? `저장 실패 — ${error.message}` : '저장 실패');
    }
  }

  /* ── 행 선택 · 뷰 전환 · 버튼들 ── */

  // 여백 카드 클릭 → 그 행을 검토됨(데모)으로 표시하고 강조.
  onRow(_e: Event, target: HTMLElement): void {
    const idx = Number(target.dataset.idx);
    if (Number.isNaN(idx)) return;
    this.data.selectedIdx = idx;
    if (!this.data.seen.includes(idx)) this.data.seen.push(idx);
    this.paintOurViewOutPaper();
  }

  onBriefToggle(): void {
    this.data.isBrief = !this.data.isBrief;   // 정식 ↔ 약식 — 서버 구성부품 펼침/문장 요약. 행 수가 바뀌니 통째 경로.
    this.refillAndRepaint();
  }

  onCustomerViewToggle(): void {
    this.data.isCustomerView = !this.data.isCustomerView;
    const root = this._rootElement?.querySelector<HTMLElement>('.quote-editor') ?? this._rootElement;
    root?.classList.toggle('customer-view', this.data.isCustomerView);
    this.refillAndRepaint();   // 편집 전용 줄(대체·우리 줄) 빼고 진짜 출력물로 — 행 수가 바뀌니 통째 경로
  }

  onSave(): void {
    alert('저장 — quote_item 편집 저장(A5 PATCH)은 후속 단계입니다.');
  }

  /** 엑셀 출력 — xlsx 는 언제나 고객 종이: 현재 뷰와 무관하게 고객 뷰로 채워 종이 모양으로 넘긴다. 화면은 그대로. 그리는 손은 백엔드(ExcelJS). */
  async onExportXlsx(): Promise<void> {
    this.status('견적서(xlsx) 생성 중…');
    try {
      const { filled } = this.fillPaper(true);                       // 고객 뷰로 채움 — 마진 열·우리 줄·판매 라벨 없음
      const customerPaper = toCustomerPaper(filled.inPaper);
      const quoteNo = this.data.header?.quote_no;
      const filename = quoteNo ? `${quoteNo}.xlsx` : `quote-${this.data.quoteId}.xlsx`;
      await api.downloadPaperXlsx(this.data.quoteId, customerPaper, filename);
      this.status('');
    } catch (error) {
      this.status(error instanceof Error ? `엑셀 출력 실패 — ${error.message}` : '엑셀 출력 실패');
    }
  }

  // 발송 게이트 — 안 본 행이 있으면 첫 안 본 행으로 점프(선택), 없으면 발송(후속).
  onSend(): void {
    const unseen = this.data.items.map((_, itemIndex) => itemIndex).filter((itemIndex) => !this.data.seen.includes(itemIndex));
    if (unseen.length) {
      this.data.selectedIdx = unseen[0];
      this.data.seen.push(unseen[0]);
      this.paintOurViewOutPaper();
      return;
    }
    alert('발송 — 모든 행 검토됨. 실제 발송(A5)은 후속 단계입니다.');
  }

  /* ── 소스 선택 (팝오버: 추천 재고·RFQ | 대체품 검색 → PATCH 로 원가·마진 실시간) ── */

  // 소스 셀 클릭 → 팝오버 열고(추천 탭, 조회 중) → 해당 행의 파트넘버로 /sources 조회.
  async onSource(_e: Event, target: HTMLElement): Promise<void> {
    const itemIndex = Number(target.dataset.idx);
    const item = this.data.items[itemIndex];
    if (!item) return;
    const picker: SourcePickerState = { itemIndex, tab: 'recommended', recommended: null, query: '', searched: null, isSearching: false };
    this.data.sourcePicker = picker;
    this.paintSourcePicker();

    const part = item.optionPart ?? '';
    try {
      picker.recommended = await fetchSourceCandidates(part ? [part] : [], this.data.dealId);
    } catch {
      picker.recommended = { stock: [], rfq: [] };
    }
    if (this.data.sourcePicker === picker) this.paintSourcePicker();   // 그새 닫혔거나 다른 행이면 안 그림
  }

  // 탭 전환 — 검색 탭이면 입력줄에 커서.
  onSourceTab(_e: Event, target: HTMLElement): void {
    const picker = this.data.sourcePicker;
    if (!picker) return;
    const tab = target.dataset.tab;
    assert(tab === 'recommended' || tab === 'search', `소스 팝오버 탭이 아닌 값: ${tab}`);
    picker.tab = tab;
    this.paintSourcePicker();
    this._rootElement?.querySelector<HTMLInputElement>('.qs-input')?.focus();
  }

  // [검색] 버튼 / 입력줄 Enter → 입력줄의 파트넘버로 대체품 소스 검색.
  onSourceSearch(): void { void this.searchSources(); }
  onSourceQueryKey(keyEvent: KeyboardEvent): void {
    if (keyEvent.key === 'Enter') { keyEvent.preventDefault(); void this.searchSources(); }
  }

  /** 입력줄 문구로 /sources?q= 조회 → 검색 탭 본문. 응답이 왔을 때 팝오버가 살아 있고 검색어가 같아야 그린다. */
  private async searchSources(): Promise<void> {
    const picker = this.data.sourcePicker;
    if (!picker) return;
    const query = this._rootElement?.querySelector<HTMLInputElement>('.qs-input')?.value.trim() ?? '';
    if (query === '') return;
    picker.query = query;
    picker.isSearching = true;
    this.paintSourcePicker();

    let searched: SourcePickerState['searched'];
    try {
      searched = await searchSourceCandidates(query, this.data.dealId);
    } catch {
      searched = { stock: [], rfq: [] };
    }
    if (this.data.sourcePicker !== picker || picker.query !== query) return;
    picker.searched = searched;
    picker.isSearching = false;
    this.paintSourcePicker();
  }

  // 후보 [선택] → quote_item 에 source_type·source_ref_id·cost_basis 저장(PATCH) → 원가·마진 갱신.
  async onSourcePick(_e: Event, target: HTMLElement): Promise<void> {
    const picker = this.data.sourcePicker;
    if (!picker) return;
    const item = this.data.items[picker.itemIndex];
    if (!item) return;
    if (item.id == null) { this.status('저장 안 된 새 품목입니다 — 소스 연결은 저장 후(후속).'); return; }
    const patch = sourcePickPatch(target.dataset, item);
    this.data.sourcePicker = null;                   // 팝오버 닫기(낙관적)
    this.paintSourcePicker();
    await this.saveSourceLink(item, patch);
  }

  /** 소스 연결 저장 — quote_item 에 source_type·source_ref_id·cost_basis PATCH → 응답으로 품목 갱신(원가·마진·우리 줄). 소스 팝오버·레퍼런스 패널 공용. */
  private async saveSourceLink(item: QuoteItem, patch: api.QuoteItemPatchBody): Promise<void> {
    assert(item.id != null, '소스 연결 저장은 저장된 품목에만');
    try {
      const editorData = await api.patchQuoteEditorItem(this.data.quoteId, item.id, patch);
      this.applyEditorData(editorData);
    } catch (error) {
      this.status(error instanceof Error ? `소스 저장 실패 — ${error.message}` : '소스 저장 실패');
    }
  }

  /* ── 품목 추가 (모달: 위치 칩 → 카탈로그 검색 → 바구니 → 완료 = POST 한 번) ── */

  // 구성 머리 "＋ 항목"(서버 id) · 하단바 "＋ 항목 추가"(개별 파트) → 모달.
  onAddOpen(_e: Event, target: HTMLElement): void {
    const serverId = target.dataset.serverId ? Number(target.dataset.serverId) : null;
    this.openAddItems(serverId);
  }

  private openAddItems(targetServerId: number | null): void {
    if (!this.data.isLoaded || this.data.isCustomerView) return;
    this.data.addItems = { targetServerId, query: '', hits: null, isSearching: false, basket: [] };
    this.paintAddItems();
    this._rootElement?.querySelector<HTMLInputElement>('.qa-input')?.focus();
  }

  onAddTarget(_e: Event, target: HTMLElement): void {
    const modal = this.data.addItems;
    if (!modal) return;
    modal.targetServerId = target.dataset.serverId ? Number(target.dataset.serverId) : null;
    this.paintAddItems();
  }

  // 타이핑 → 250ms 쉬면 카탈로그 검색. 늦게 온 응답은 검색어가 달라졌으면 버린다.
  onAddQueryInput(_e: Event, target: HTMLElement): void {
    const modal = this.data.addItems;
    if (!modal) return;
    modal.query = (target as HTMLInputElement).value;
    if (this._addSearchTimer) window.clearTimeout(this._addSearchTimer);
    this._addSearchTimer = window.setTimeout(() => { void this.searchCatalog(modal); }, 250);
  }

  private async searchCatalog(modal: AddItemsState): Promise<void> {
    const query = modal.query.trim();
    if (query === '') { modal.hits = null; this.paintAddItems(); return; }
    modal.isSearching = true;
    this.paintAddItemsKeepingInput();

    let hits: AddItemsState['hits'];
    try {
      hits = (await searchAll(query)).catalog;
    } catch {
      hits = [];
    }
    if (this.data.addItems !== modal || modal.query.trim() !== query) return;
    modal.hits = hits;
    modal.isSearching = false;
    this.paintAddItemsKeepingInput();
  }

  onAddQueryKey(keyEvent: KeyboardEvent): void {
    if (keyEvent.key === 'Escape') this.onAddClose();
  }

  // 결과 줄 [＋ 담기] → 바구니(같은 파트넘버는 한 번만). 카테고리는 출처 테이블을 견적 어휘로 — 지면 순서 규칙이 먹게.
  onAddToBasket(_e: Event, target: HTMLElement): void {
    const modal = this.data.addItems;
    if (!modal) return;
    const optionPart = target.dataset.part ?? '';
    assert(optionPart !== '', '담을 항목에 파트넘버가 없다');
    if (modal.basket.some((item) => item.optionPart === optionPart)) return;
    const newItem: NewItem = {
      optionPart,
      description: target.dataset.desc || null,
      category: toSpecCategory(target.dataset.source) ?? target.dataset.source ?? null,
      quantity: 1,
    };
    modal.basket.push(newItem);
    this.paintAddItemsKeepingInput();
  }

  onAddRemoveFromBasket(_e: Event, target: HTMLElement): void {
    const modal = this.data.addItems;
    if (!modal) return;
    modal.basket = modal.basket.filter((item) => item.optionPart !== target.dataset.part);
    this.paintAddItemsKeepingInput();
  }

  onAddQuantity(_e: Event, target: HTMLElement): void {
    const modal = this.data.addItems;
    if (!modal) return;
    const item = modal.basket.find((basketItem) => basketItem.optionPart === target.dataset.part);
    if (!item) return;
    item.quantity = Math.max(1, item.quantity + Number(target.dataset.delta));
    this.paintAddItemsKeepingInput();
  }

  // [완료] → 바구니를 한 번에 POST → 응답(편집 데이터 전체)으로 재채움 — 새 부품은 제 카테고리 자리로 → 닫기.
  async onAddConfirm(): Promise<void> {
    const modal = this.data.addItems;
    if (!modal || modal.basket.length === 0) return;
    const parentId = modal.targetServerId != null ? String(modal.targetServerId) : null;
    const body: api.NewQuoteItemBody[] = modal.basket.map((item) => ({
      category: item.category, option_part: item.optionPart, description: item.description,
      quantity: item.quantity, parent_quote_item_id: parentId,
    }));
    this.status(`품목 ${body.length}개 추가 중…`);
    try {
      const editorData = await api.addQuoteEditorItems(this.data.quoteId, body);
      this.data.addItems = null;
      this.paintAddItems();
      this.applyEditorData(editorData);
      this.status(`품목 ${body.length}개 추가됨`);
    } catch (error) {
      this.status(error instanceof Error ? `품목 추가 실패 — ${error.message}` : '품목 추가 실패');
    }
  }

  onAddClose(): void {
    this.data.addItems = null;
    this.paintAddItems();
  }

  /** 모달을 살림대로 — 열기/닫기 전환은 상태 바꾸고 이 함수 한 번(닫힘 = 빈 문자열). */
  private paintAddItems(): void {
    this.refreshRegion('.qe-add-items-slot', () => this.renderAddItemsOverlay());
  }

  /** 입력 중 다시 그리기 — 통째 교체하면 입력줄 포커스·커서가 날아가므로, 그린 뒤 커서를 끝으로 되돌린다. */
  private paintAddItemsKeepingInput(): void {
    this.paintAddItems();
    const input = this._rootElement?.querySelector<HTMLInputElement>('.qa-input');
    if (input) { input.focus(); input.setSelectionRange(input.value.length, input.value.length); }
  }

  private renderAddItemsOverlay(): string {
    const modal = this.data.addItems;
    if (!modal) return '';
    return renderAddItemsModal(modal, this.serverChips());
  }

  /** 서버 칩들 — 서버 견적의 서버 행들(allItems 의 lineType 'server'). 파트 견적이면 빈 배열(칩 줄 없음). */
  private serverChips(): ServerChip[] {
    return this.data.allItems
      .filter((item) => item.lineType === 'server' && item.id != null)
      .map((server, serverIndex) => ({ id: server.id as number, label: `서버 ${String(serverIndex + 1).padStart(2, '0')} · ${server.description ?? '서버'}` }));
  }

  onSourceClose(): void {
    this.data.sourcePicker = null;
    this.paintSourcePicker();
  }

  onSourceRfq(): void {
    alert('RFQ 요청 — 소싱 니즈 생성은 후속(A4). 이 행의 소스가 없을 때 매입처에 견적을 띄웁니다.');
  }

  /** 소스 선택 팝오버를 상태대로 — 열기/로딩/닫기 전환은 상태 바꾸고 이 함수 한 번이 전부(닫힘 = 빈 문자열). */
  private paintSourcePicker(): void {
    this.refreshRegion('.qe-source-slot', () => this.renderSourcePickerOverlay());
  }

  /** 팝오버 HTML — 닫힘('') 아니면 살림(sourcePicker) 그대로 renderSourcePicker 에(조회 중·탭·검색 결과는 살림이 말한다). */
  private renderSourcePickerOverlay(): string {
    const picker = this.data.sourcePicker;
    if (!picker) return '';
    const item = this.data.items[picker.itemIndex];
    if (!item) return '';
    return renderSourcePicker(item.optionPart ?? '', item.description, picker, item.sourceRefId);
  }
}

/* ═══════════════ ④ 편집 재료 — 입력기·파싱·키 연결 (View 밖 조각들) ═══════════════ */

/** 편집 입력기 — 종류가 정한다: 컨디션은 드롭다운, 그 외는 input. */
function editorInputFor(kind: string | undefined, field: keyof QuoteItem, currentValue: string | number | null): HTMLInputElement | HTMLSelectElement {
  if (kind === 'picked' && field === 'condition') return conditionSelect(currentValue);
  const inputEl = document.createElement('input');
  inputEl.value = currentValue == null ? '' : String(currentValue);
  return inputEl;
}

/** 컨디션 드롭다운 — 선택지 New/RF/REF/F/S + 빈 값('—'), 현재 값에 체크. */
function conditionSelect(currentValue: string | number | null): HTMLSelectElement {
  const selectEl = document.createElement('select');
  for (const conditionName of ['', 'New', 'RF', 'REF', 'F/S']) {
    const optionEl = document.createElement('option');
    optionEl.value = conditionName; optionEl.textContent = conditionName || '—';
    if (conditionName === (currentValue ?? '')) optionEl.selected = true;
    selectEl.appendChild(optionEl);
  }
  return selectEl;
}

/** 칸을 비우고 입력기를 심어 포커스 — 텍스트 input 은 전체 선택. */
function plantInput(target: HTMLElement, input: HTMLInputElement | HTMLSelectElement): void {
  target.textContent = '';
  target.appendChild(input);
  input.focus();
  (input as HTMLInputElement).select?.();
}

/** 입력기 키 연결 — Enter/Tab 은 커밋(+이동 방향), Escape 는 취소, blur 는 커밋(이동 없음). */
function connectEditorKeys(
  input: HTMLInputElement | HTMLSelectElement,
  commit: (dir: number) => void, cancel: () => void,
): void {
  input.addEventListener('keydown', (ev: Event) => {
    const ke = ev as KeyboardEvent;
    if (ke.key === 'Enter') { ke.preventDefault(); commit(1); }
    else if (ke.key === 'Tab') { ke.preventDefault(); commit(ke.shiftKey ? -1 : 1); }
    else if (ke.key === 'Escape') cancel();
  });
  input.addEventListener('blur', () => commit(0));
}

/** 입력 문구 → 필드 값. 빈 문구 = null · number 는 콤마/공백 제거, 못 읽으면 원래 값 유지. */
function parseEditValue(kind: string | undefined, rawValue: string, currentValue: string | number | null): string | number | null {
  const raw = rawValue.trim();
  if (raw === '') return null;
  if (kind !== 'number') return raw;
  const parsed = Number(raw.replace(/[,\s]/g, ''));
  return Number.isNaN(parsed) ? currentValue : parsed;
}

/** 소스 후보의 dataset → PATCH 바디 — 원가 3필드 + (다른 파트넘버면) 요청→공급 대체 기록. */
function sourcePickPatch(dataset: DOMStringMap, item: QuoteItem): api.QuoteItemPatchBody {
  const amount = dataset.amt ? Number(dataset.amt) : null;
  const patch: api.QuoteItemPatchBody = {
    source_type: (dataset.type as api.QuoteItemPatchBody['source_type']) ?? null,
    source_ref_id: dataset.ref || null,
    cost_basis_amount: amount != null && Number.isFinite(amount) ? amount : null,
    cost_basis_currency: dataset.cur || null,
  };
  const partNo = dataset.part || '';
  if (partNo && partNo !== item.optionPart) {   // 다른 파트넘버 소스(검색·대체품) → 대체로 기록
    patch.requested_part = item.requestedPart ?? item.optionPart;
    patch.option_part = partNo;
    if (dataset.cond) patch.requested_condition = dataset.cond;
  }
  return patch;
}

/* ═══════════════ ⑤ 지면 비교 — paintPaper 의 순수 조각 ═══════════════ */

/** 행 높이 나열이 같은가 — 같아야 이전/새 지면의 행끼리 1:1 교체가 성립한다(paintPaper). */
function sameRowHeights(prev: InPaper, next: InPaper): boolean {
  return prev.rows.length === next.rows.length
    && prev.rows.every((heightPx, rowIndex) => heightPx === next.rows[rowIndex]);
}

/* ═══════════════ ⑥ CSS — 셸 전용 (편집/지면 CSS 와 분리) ═══════════════ */

const BASE_CSS = `
.quote-editor{min-height:100vh;background:#e9edf0}
.qe-status{font:14px/1.6 system-ui,sans-serif;color:#5a6771;padding:14px 20px}
.qe-slot{display:contents}
`;
